import clsx from 'clsx';
import styles from './Slide.module.scss';
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent,
} from 'react';

export type SlideDirection = 'bottom' | 'left' | 'right' | 'top';

const OPPOSITE_DIRECTION: Record<SlideDirection, SlideDirection> = {
  left: 'right',
  right: 'left',
  bottom: 'top',
  top: 'bottom',
};

interface ChildrenArgs {
  hide: (direction: SlideDirection) => void;
  show: (direction: SlideDirection) => void;
}

interface Props {
  children: ReactNode | ((args: ChildrenArgs) => ReactNode);
  direction?: SlideDirection;
  // `null` is a third, pre-entrance state: offscreen on the OPPOSITE side
  // of `direction` from where `false` (hidden) sits. That way a caller
  // can move the same component through `null -> true` (entering) using
  // the same `direction` another instance used for its own `true ->
  // false` (exiting), and both read as one continuous motion instead of
  // needing to flip `direction` themselves. Omitted/`undefined` behaves
  // exactly as `true` always has.
  visible?: boolean | null;
  onHideEnd?: () => void;
  onShowEnd?: () => void;
}

// Safari does not reliably fire `transitionend` for every transition (e.g.
// when the before/after styles resolve within the same layout/paint cycle),
// which would otherwise leave onShowEnd/onHideEnd never called. This
// fallback timer guarantees they still fire, matched to the CSS duration
// (0.4s in Slide.module.scss) plus a small buffer so the real event wins
// when it does fire.
const TRANSITION_FALLBACK_MS = 450;

export function Slide({
  children,
  direction,
  onHideEnd,
  onShowEnd,
  visible: visibleProp,
}: Props) {
  const [transition, setTransition] = useState<string>();
  const [visible, setVisible] = useState<boolean>(false);
  // True only while resting at the `null` pre-entrance position — that
  // position is set up immediately, never animated. `true`/`false` are
  // the only states that transition over the CSS duration.
  const [instant, setInstant] = useState(false);
  const previousVisibleRef = useRef<boolean | null | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(null);

  function hide(direction: SlideDirection) {
    setTransition('out-' + direction);
    setVisible(false);
    setInstant(false);
  }

  function show(direction: SlideDirection) {
    setTransition('in-' + direction);
    setVisible(true);
    setInstant(false);
  }

  // The pre-entrance resting position for `visible={null}` — offscreen on
  // the opposite side from `hide`, reusing the same `out-*` classes (their
  // CSS is identical to `in-*`; only the side differs). Applied instantly,
  // with no transition.
  function enter(direction: SlideDirection) {
    setTransition('out-' + OPPOSITE_DIRECTION[direction]);
    setVisible(false);
    setInstant(true);
  }

  function clearFallback() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }

  useLayoutEffect(() => {
    const nextVisible = visibleProp === undefined ? true : visibleProp;
    const isFirstRun = previousVisibleRef.current === undefined;
    const didChange = !isFirstRun && previousVisibleRef.current !== nextVisible;
    previousVisibleRef.current = nextVisible;

    setVisible(nextVisible === true);
    if (direction) {
      if (nextVisible === true) {
        show(direction);
      } else if (nextVisible === false) {
        hide(direction);
      } else {
        enter(direction);
      }
    }

    clearFallback();

    // Only arm the fallback when the resolved visible value actually
    // changed. This is deliberately a value comparison rather than a
    // "have we mounted" flag: React 18 StrictMode replays this effect
    // (mount -> cleanup -> mount again) with identical props, and a simple
    // mounted-flag would treat that synthetic replay as a real transition
    // and fire onShowEnd/onHideEnd for nothing.
    if (!didChange) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = undefined;
      // `null` (entering) has no end callback of its own — it's a resting
      // pre-entrance position, not a transition anyone listens for.
      if (nextVisible === true) {
        onShowEnd?.();
      } else if (nextVisible === false) {
        onHideEnd?.();
      }
    }, TRANSITION_FALLBACK_MS);

    return clearFallback;
    // onShowEnd/onHideEnd are read at trigger time on purpose - including
    // them here would re-arm the fallback on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, visibleProp]);

  function onTransitionEnd(event: TransitionEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) {
      return;
    }
    clearFallback();
    (visible ? onShowEnd : onHideEnd)?.();
  }

  return (
    <div
      className={clsx(
        styles['slide'],
        transition && styles[transition],
        visible && styles['visible'],
        instant && styles['instant'],
      )}
      onTransitionEnd={onTransitionEnd}
    >
      {typeof children === 'function' ? children({ hide, show }) : children}
    </div>
  );
}
